import { t } from "i18next";
import { Play } from "lucide-react";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { cn } from "@/core/utils/func/cn";
import type { KycCaseDto, PartyDto } from "@/features/kyc/api/dto";
import type { WalletDto } from "@/features/wallet/api/dto";
import {
  KYC_STATUSES,
  PARTY_TYPES,
  SANCTIONS_SCREENING_STATUSES,
} from "@/features/shared/api/enums";

/**
 * Ligne de la liste « Parcours en cours » : une Party sans portefeuille
 * ACTIVE et non REJECTED (critères appliqués côté client — le filtre serveur
 * `kycStatus` exclut les Parties NONE), croisée en mémoire avec son
 * portefeuille et son dossier KYC les plus récents.
 */
export interface OnboardingDraftRow {
  id: string;
  party: PartyDto;
  wallet: WalletDto | null;
  kycCase: KycCaseDto | null;
  /** Étape atteinte du parcours (1 à 7) — voir `onboardingDraftStep`. */
  step: number;
}

/**
 * Étape atteinte, dérivée des données serveur (même logique de libellés que
 * `stepStates` de la page onboarding) :
 * case APPROVED → 7, criblage CLEAR → 6, case SUBMITTED/IN_REVIEW → 5,
 * case DRAFT → 4, portefeuille existant → 3, sinon → 2 (Party créée).
 */
export const onboardingDraftStep = (
  party: PartyDto,
  wallet: WalletDto | null,
  kycCase: KycCaseDto | null
): number => {
  if (kycCase?.status === "APPROVED") return 7;
  if (party.sanctionsScreeningStatus === "CLEAR") return 6;
  if (kycCase?.status === "SUBMITTED" || kycCase?.status === "IN_REVIEW")
    return 5;
  if (kycCase?.status === "DRAFT") return 4;
  if (wallet) return 3;
  return 2;
};

/** Couleurs de statut par tokens (règle projet : surfaces `-muted`, jamais de palette brute). */
const KYC_STATUS_STYLES: Record<string, string> = {
  NONE: "border-info/40 bg-info-muted text-info",
  PENDING: "border-warning/40 bg-warning-muted text-warning",
  TIER1: "border-success/40 bg-success-muted text-success",
  TIER2: "border-success/40 bg-success-muted text-success",
  REJECTED: "border-destructive/40 bg-destructive/10 text-destructive",
};

const SCREENING_STYLES: Record<string, string> = {
  NOT_SCREENED: "border-border bg-muted text-muted-foreground",
  CLEAR: "border-success/40 bg-success-muted text-success",
  HIT: "border-destructive/40 bg-destructive/10 text-destructive",
  UNDER_REVIEW: "border-warning/40 bg-warning-muted text-warning",
};

const NEUTRAL_STYLE = "border-border bg-muted text-muted-foreground";

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Colonnes de la liste « Parcours en cours » (brouillons serveur du parcours
 * d'inscription). Données PII et identifiants en `NoTranslate`. L'action
 * « Reprendre » renvoie vers la vue wizard avec les identifiants dans l'URL.
 */
export const onboardingDraftsColumns: ColumnsClarFactory<OnboardingDraftRow> = (
  navigate
) => [
  {
    id: "client",
    accessorFn: (row) =>
      [row.party.firstName, row.party.lastName].filter(Boolean).join(" ").trim(),
    header: t("kyc.onboarding.drafts.columns.client"),
    cell: ({ row, getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || `${row.original.id.slice(0, 8)}…`}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "partyType",
    accessorFn: (row) => row.party.partyType,
    header: t("kyc.onboarding.drafts.columns.type"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(PARTY_TYPES) },
  },
  {
    id: "kycStatus",
    accessorFn: (row) => row.party.kycStatus ?? "",
    header: t("kyc.onboarding.drafts.columns.kycStatus"),
    cell: ({ getValue }) => {
      const value = (getValue() as string) || "—";
      return (
        <NoTranslate as="span">
          <Badge
            variant="outline"
            className={cn(KYC_STATUS_STYLES[value] ?? NEUTRAL_STYLE)}
          >
            {value}
          </Badge>
        </NoTranslate>
      );
    },
    meta: { type: "select", options: selectOptions(KYC_STATUSES) },
  },
  {
    id: "sanctionsScreeningStatus",
    accessorFn: (row) => row.party.sanctionsScreeningStatus ?? "",
    header: t("kyc.onboarding.drafts.columns.screening"),
    cell: ({ getValue }) => {
      const value = (getValue() as string) || "—";
      return (
        <NoTranslate as="span">
          <Badge
            variant="outline"
            className={cn(SCREENING_STYLES[value] ?? NEUTRAL_STYLE)}
          >
            {value}
          </Badge>
        </NoTranslate>
      );
    },
    meta: {
      type: "select",
      options: selectOptions(SANCTIONS_SCREENING_STATUSES),
    },
  },
  {
    id: "step",
    accessorKey: "step",
    header: t("kyc.onboarding.drafts.columns.step"),
    cell: ({ getValue }) => {
      const step = getValue() as number;
      return (
        <span className="text-sm">
          {step}. {t(`kyc.onboarding.steps.${step - 1}.title`)}
        </span>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("kyc.onboarding.drafts.columns.actions"),
    enableHiding: false,
    cell: ({ row }) => {
      const params = new URLSearchParams({ partyId: row.original.id });
      if (row.original.wallet?.id)
        params.set("walletId", row.original.wallet.id);
      if (row.original.kycCase?.id)
        params.set("kycCaseId", row.original.kycCase.id);
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/app/kyc/onboarding?${params.toString()}`)}
        >
          <Play className="mr-1.5 h-3.5 w-3.5" />
          {t("kyc.onboarding.drafts.resume")}
        </Button>
      );
    },
    meta: { type: "denied" },
  },
];
